import React from 'react';
import { Button, Modal, Table, Form } from 'react-bootstrap';
import ShipmentApi from '../../api/ShipmentApi';
import AuthorizedPage from '../../component/AuthorizedPage';

type DriverStatus = 'AVAILABLE' | 'LOAD_WEIGHT_CHECK' | 'ON_DELIVERY' | 'ON_VACATION';

interface Driver {
  id?: number;
  name: string;
  surname: string;
  gender: string;
  dateOfBirth: string; // ISO date string: "YYYY-MM-DD" veya "YYYY-MM-DDTHH:mm:ss.sssZ"
  phone: string;
  tckn: string;
  licenceNumber: string;
  status: DriverStatus;
}

interface State {
  drivers: Driver[];
  showModal: boolean;
  editingDriver: Driver | null;
}

const defaultDriver: Driver = {
  name: '',
  surname: '',
  gender: '',
  dateOfBirth: '',
  phone: '',
  tckn: '',
  licenceNumber: '',
  status: 'AVAILABLE',
};

export default class DriverManagement extends React.Component<{}, State> {
  constructor(props: {}) {
    super(props);
    this.state = {
      drivers: [],
      showModal: false,
      editingDriver: null,
    };
  }

  componentDidMount() {
    this.loadDrivers();
  }

  async loadDrivers() {
    const result = await ShipmentApi.listDrivers(); // Backend'de buna karşılık gelen endpoint olmalı
    if (result.success && result.data) {
      // Tarihlerde saat varsa sadece tarih kısmını alarak set et
      const cleanData = result.data.map((driver: Driver) => ({
        ...driver,
        dateOfBirth: driver.dateOfBirth ? driver.dateOfBirth.split('T')[0] : '',
      }));
      this.setState({ drivers: cleanData });
    } else {
      console.error("Sürücüler yüklenemedi:", result.error);
      this.setState({ drivers: [] });
    }
  }

  openEditModal = (driver?: Driver) => {
    this.setState({
      editingDriver: driver ? { ...driver } : { ...defaultDriver },
      showModal: true,
    });
  };

  handleChange = (e: React.ChangeEvent<any>) => {
    if (!this.state.editingDriver) return;
    const { name, value } = e.target;
    this.setState(prevState => ({
      editingDriver: {
        ...prevState.editingDriver!,
        [name]: value,
      },
    }));
  };

  handleSave = async () => {
    const { editingDriver } = this.state;
    if (!editingDriver) return;

    // Backend'e sadece tarih olarak gönder
    const cleanedDriver = {
      ...editingDriver,
      dateOfBirth: editingDriver.dateOfBirth.split('T')[0],
    };

    await ShipmentApi.createOrUpdateDriver(cleanedDriver); // Backend endpointine uygun olarak
    this.setState({ showModal: false, editingDriver: null }, () => this.loadDrivers());
  };

  renderDriverTable() {
    return (
      <Table striped bordered hover size="sm">
        <thead>
          <tr>
            <th>Ad</th>
            <th>Soyad</th>
            <th>Cinsiyet</th>
            <th>Doğum Tarihi</th>
            <th>Telefon</th>
            <th>TCKN</th>
            <th>Sürücü Belge No</th>
            <th>Durum</th>
            <th>İşlem</th>
          </tr>
        </thead>
        <tbody>
          {this.state.drivers.map(driver => (
            <tr key={driver.id}>
              <td>{driver.name}</td>
              <td>{driver.surname}</td>
              <td>{driver.gender}</td>
              <td>{driver.dateOfBirth}</td>
              <td>{driver.phone}</td>
              <td>{driver.tckn}</td>
              <td>{driver.licenceNumber}</td>
              <td>{driver.status}</td>
              <td>
                <Button size="sm" variant="outline-primary" onClick={() => this.openEditModal(driver)}>
                  Düzenle
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

  renderDriverModal() {
    const { editingDriver } = this.state;
    if (!editingDriver) return null;

    return (
      <Modal show={this.state.showModal} onHide={() => this.setState({ showModal: false })}>
        <Modal.Header closeButton>
          <Modal.Title>Sürücü Bilgileri</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Ad</Form.Label>
              <Form.Control name="name" value={editingDriver.name} onChange={this.handleChange} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Soyad</Form.Label>
              <Form.Control name="surname" value={editingDriver.surname} onChange={this.handleChange} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Cinsiyet</Form.Label>
              <Form.Select name="gender" value={editingDriver.gender} onChange={this.handleChange}>
                <option value="">Seçiniz</option>
                <option value="Erkek">Erkek</option>
                <option value="Kadın">Kadın</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Doğum Tarihi</Form.Label>
              <Form.Control
                type="date"
                name="dateOfBirth"
                value={editingDriver.dateOfBirth}
                onChange={this.handleChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Telefon</Form.Label>
              <Form.Control name="phone" value={editingDriver.phone} onChange={this.handleChange} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>TCKN</Form.Label>
              <Form.Control name="tckn" value={editingDriver.tckn} onChange={this.handleChange} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Sürücü Belge No</Form.Label>
              <Form.Control name="licenceNumber" value={editingDriver.licenceNumber} onChange={this.handleChange} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Durum</Form.Label>
              <Form.Select name="status" value={editingDriver.status} onChange={this.handleChange}>
                <option value="AVAILABLE">Müsait</option>
                <option value="LOAD_WEIGHT_CHECK">Yük Kontrolünde</option>
                <option value="ON_DELIVERY">Teslimatta</option>
                <option value="ON_VACATION">İzinli</option>
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => this.setState({ showModal: false })}>
            İptal
          </Button>
          <Button variant="primary" onClick={this.handleSave}>
            Kaydet
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  render() {
    return (
      <AuthorizedPage role="ORDER_MANAGER" isLoading={false}>
        <div className="container mt-4">
          <h4>Sürücü Yönetimi</h4>
          <Button className="mb-3" onClick={() => this.openEditModal()}>
            + Sürücü Ekle
          </Button>
          {this.renderDriverTable()}
          {this.renderDriverModal()}
        </div>
      </AuthorizedPage>
    );
  }
}
